import {gql} from '@apollo/client';

import {SchemaTag} from 'folder-items/FileConstants';
import {doQuerySafe, readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ResultsRowMetadata} from 'notebook/integrations/results/ResultsTableConstants';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ColumnGroup} from '../columns/ColumnDefinitions';
import {LiveCellValue} from '../LiveCellValues';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from '../StructuredTableConstants';
import {
  BulkResultLiveDataQuery,
  BulkResultLiveDataQueryVariables,
} from './__generated/BulkResultLiveDataQuery';
import {buildStandardUpdatedCells} from './updateRowUtils';

type ResultInfo = ExtractByTypename<BulkResultLiveDataQuery['results'][number], 'ResultNode'>;

export const BULK_RESULT_LIVE_DATA_QUERY = gql`
  query BulkResultLiveDataQuery($resultIds: [StrictString!]!) @skipAudit @engComponent(name: RESULTS) {
    results(ids: $resultIds) {
      ...ResultGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('Result').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<BulkResultLiveDataQuery['results']> {
  const {results} = await doQuerySafe<BulkResultLiveDataQuery, BulkResultLiveDataQueryVariables>(
    {query: BULK_RESULT_LIVE_DATA_QUERY},
    {
      variables: {
        resultIds: sampleLookupInfos.map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  return results;
}

/**
 * The result's live value for a column, regardless of whether the result is archived.
 *
 * Agent keep in sync: ResultsRowUpdater._live_tag_value in
 * benchling/notebook/structured_tables/resync/row_updaters/results.py
 */
function getLiveTagValue(
  model: ResultInfo,
  columnGroup: ColumnGroup | null,
  column: StructuredTableColumn
): LiveCellValue {
  const matchingTag = model.tagsJson.find(
    (tag: SchemaTag) => tag.tagSchemaField.apiId === column.schemaFieldApiId
  );
  return matchingTag != null ? convertTagToTypedValue(matchingTag) : 'NO_VALUE';
}

export const ResultsTableRowUpdater: StructuredTableRowUpdater<ResultInfo, ResultsRowMetadata> = {
  fetchModel(sampleLookupInfo: SampleLookupInfo): Promise<ResultInfo | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(sampleLookupInfos: Array<SampleLookupInfo>): Promise<BulkResultLiveDataQuery['results']> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache({idForLookup: resultId}: SampleLookupInfo): ResultInfo | null {
    return readFragmentFromCache(getFragmentWrapper('Result'), resultId);
  },

  updateCell: (
    model: ResultInfo | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue => {
    // Only write into cells if the result is accessible and unarchived. An archived result's live values
    // still feed the LSUV snapshot; see getLsuvSourceRow.
    if (isInaccessible(model) || model.archiveRecord != null) {
      return 'NO_VALUE';
    }
    return getLiveTagValue(model, columnGroup, column);
  },

  getLsuvSourceRow(
    model: ResultInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ResultsRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<ResultsRowMetadata> | null {
    if (isInaccessible(model) || model.archiveRecord == null) {
      return null;
    }
    // An archived result keeps its cells (see updateRow), so reading the LSUV snapshot off them would record
    // whatever the row already displayed rather than the live value the resync fetched -- and for a formula
    // cell, which carries no typedValue, it would clear the LSUV outright. The archived result's tag values
    // are still live upstream values, so snapshot those instead.
    return {
      ...row,
      cells: buildStandardUpdatedCells(model, columns, row, columnGroup, getLiveTagValue),
    };
  },

  updateRow(
    model: ResultInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ResultsRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<ResultsRowMetadata> {
    if (isInaccessible(model)) {
      return row;
    }

    if (model.archiveRecord != null) {
      // Early return row metadata without updating cells for archived results.
      return {...row, lastSyncedArchivePurpose: model.archiveRecord.purpose};
    }

    return {
      ...row,
      lastSyncedArchivePurpose: null,
      cells: buildStandardUpdatedCells(model, columns, row, columnGroup, this.updateCell),
    };
  },
};
